import dayjs from 'dayjs';
import { difference, isEqual } from 'lodash';
import { useCallback, useMemo, useState } from 'react';

import { pluralizeAreas } from 'src/utils/mappers/eventForm/area.mapper';
import { mapContactChangePreview } from 'src/utils/mappers/eventForm/contact.mapper';

// TODO: закончить с "изменить контакт"

const chips = {
	create: {
		label: 'Будет создано',
		color: 'success',
	},
	remove: {
		label: 'Будет снято',
		color: 'error',
	},
	change: {
		label: 'Будет изменено',
		color: 'warning',
	},
};

const paramsChips = {
	create: {
		label: 'Будет добавлено',
		color: 'success',
	},
	change: {
		label: 'Будет изменено',
		color: 'warning',
	},
	remove: {
		label: 'Будет удалено',
		color: 'error',
	},
};

const formatParamsDate = (value) => {
	if (value == null || value === '') return null;

	const parsed = dayjs.isDayjs(value) ? value : dayjs(value);

	if (!parsed.isValid()) return null;

	return parsed.format('DD MMM YYYY');
};

const createInitialState = () => [
	{
		id: 1,
		title: 'Возражения',
		typos: [],
	},
	{
		id: 2,
		title: 'Контакты',
		typos: [],
	},
	{
		id: 3,
		title: 'Документы',
		typos: [],
	},
	{
		id: 4,
		title: 'Параметры площадки',
		typos: [],
	},
	{
		id: 5,
		title: 'Связанные площадки',
		typos: [],
	},
];

const typeHandlers = {
	objection: (action, data) => {
		const { objection, comment } = data;
		const text = action === 'create' ? comment : objection.remark;

		return {
			groupIndex: 0,
			title: objection.label,
			typoData: {
				texts: [
					{
						text,
						isThrough: action === 'remove',
					},
				],
			},
		};
	},

	contact: (action, data) => {
		if (action === 'change') {
			return mapContactChangePreview(data);
		}

		const {
			name,
			secondName,
			thirdName,
			contacts,
			decisionMaker,
			job,
			comment,
		} = data;

		return {
			groupIndex: 1,
			title: `${secondName} ${name} ${thirdName}`,
			typoData: {
				options: [
					{
						label: 'Телефон',
						value: contacts
							.map(({ text, type }) =>
								type?.label ? `${text} (${type.label})` : text,
							)
							.join('\n'),
					},
					{
						label: 'Должность',
						value: job.label,
					},
					{
						label: 'Лицо, принимающее решения',
						value: decisionMaker === '1' ? 'Да' : 'Нет',
					},
					comment && {
						label: 'Комментарий',
						value: comment,
					},
				].filter(Boolean),
			},
		};
	},

	number: (action, data) => {
		if (action !== 'create') return null;

		const { phone, comment } = data;

		return {
			groupIndex: 1,
			title: phone,
			typoData: {
				options: [
					comment && {
						label: 'Комментарий',
						value: comment,
					},
				].filter(Boolean),
			},
		};
	},

	contract: (_, data) => {
		const { title, signed, files } = data;

		return {
			groupIndex: 2,
			title,
			typoData: {
				options: [
					{
						label: 'Файл',
						value: files?.[0]?.name,
					},
					{
						label: 'Договор подписан',
						value: signed === '1' ? 'Да' : 'Нет',
					},
				],
			},
		};
	},

	doc: (_, data) => {
		const { title, files } = data;

		return {
			groupIndex: 2,
			title,
			typoData: {
				options: [
					{
						label: 'Файл',
						value: files?.[0]?.name,
					},
				],
			},
		};
	},

	params: (_, data, initialData = {}) => {
		const { date, emails, sites } = data;
		const knownFrom = initialData?.knownFrom;
		const initialEmails = (initialData.emails ?? [])
			.map((item) => String(item).trim())
			.filter(Boolean);
		const nextEmails = (emails ?? [])
			.map((item) => String(item).trim())
			.filter(Boolean);
		const initialSites = (initialData.sites ?? [])
			.map((item) => String(item).trim())
			.filter(Boolean);
		const nextSites = (sites ?? [])
			.map((item) => String(item).trim())
			.filter(Boolean);

		const added = [];
		const changed = [];
		const removed = [];

		const nextDateLabel = formatParamsDate(date);
		const prevDateLabel = formatParamsDate(knownFrom);

		if (!prevDateLabel && nextDateLabel) {
			added.push({
				id: 1,
				label: 'Известен с',
				value: nextDateLabel,
			});
		} else if (
			prevDateLabel &&
			nextDateLabel &&
			prevDateLabel !== nextDateLabel
		) {
			changed.push({
				id: 1,
				label: 'Известен с',
				value: nextDateLabel,
				old: prevDateLabel,
			});
		}

		const pushListChange = (id, label, oldList, newList) => {
			if (isEqual(oldList, newList)) return;

			const addedItems = difference(newList, oldList);
			const removedItems = difference(oldList, newList);

			if (addedItems.length && removedItems.length) {
				const changeCount = Math.min(
					addedItems.length,
					removedItems.length,
				);
				const changedAdded = addedItems.slice(0, changeCount);
				const changedRemoved = removedItems.slice(0, changeCount);
				const onlyAdded = addedItems.slice(changeCount);
				const onlyRemoved = removedItems.slice(changeCount);

				changed.push({
					id,
					label,
					value: changedAdded.join(' • '),
					old: changedRemoved.join(' • '),
				});

				if (onlyAdded.length) {
					added.push({
						id,
						label,
						value: onlyAdded.join(' • '),
					});
				}

				if (onlyRemoved.length) {
					removed.push({
						id,
						label,
						old: onlyRemoved.join(' • '),
					});
				}
				return;
			}

			if (addedItems.length) {
				added.push({
					id,
					label,
					value: addedItems.join(' • '),
				});
				return;
			}

			if (removedItems.length) {
				removed.push({
					id,
					label,
					old: removedItems.join(' • '),
				});
			}
		};

		pushListChange(2, 'Эл. почта', initialEmails, nextEmails);
		pushListChange(3, 'Ссылки', initialSites, nextSites);

		const entries = [
			added.length && {
				chip: paramsChips.create,
				typoData: { options: added },
			},
			changed.length && {
				chip: paramsChips.change,
				typoData: { options: changed },
			},
			removed.length && {
				chip: paramsChips.remove,
				typoData: { options: removed },
			},
		].filter(Boolean);

		if (!entries.length) return null;

		return {
			groupIndex: 3,
			entries,
		};
	},

	companyGroup: (action, data) => {
		const areas = (data.areas || []).filter((area) => area?.value);

		if (!areas.length) return null;

		const isLink = action === 'link';

		return {
			groupIndex: 4,
			title: pluralizeAreas(areas.length),
			typoData: {
				chip: {
					label: isLink
						? areas.length === 1
							? 'Будет прикреплена'
							: 'Будет прикреплены'
						: areas.length === 1
							? 'Будет откреплена'
							: 'Будет откреплены',
					color: isLink ? 'success' : 'error',
				},
				options: areas.map((area, index) => ({
					id: index + 1,
					value: area.label,
				})),
			},
		};
	},
};

const applyPayloadItem = (groups, item, { force = false } = {}) => {
	const handler = typeHandlers[item.type];
	let result = handler?.(item.action, item.data, item.initialData);

	if (item.action === 'change' && handler && !result && !force) {
		return { groups, skipped: true };
	}

	if (
		!result &&
		force &&
		item.type === 'contact' &&
		item.action === 'change'
	) {
		result = item.data?.target?.isPerson
			? typeHandlers.contact('create', item.data)
			: {
				groupIndex: 1,
				title: item.data?.phone || item.data?.target?.label,
				typoData: {
					options: [
						item.data?.phone && {
							label: 'Телефон',
							value: item.data.phone,
						},
						item.data?.comment && {
							label: 'Комментарий',
							value: item.data.comment,
						},
						item.data?.status && {
							label: 'Статус',
							value: item.data.status.label,
						},
					].filter(Boolean),
				},
			};
	}

	if (!result && force && item.type === 'params') {
		result = typeHandlers.params(
			item.action,
			item.data,
			item.initialData || {},
		);

		if (!result) {
			const { date, emails = [], sites = [] } = item.data || {};
			const options = [];
			const dateLabel = formatParamsDate(date);

			if (dateLabel) {
				options.push({
					id: 1,
					label: 'Известен с',
					value: dateLabel,
				});
			}

			if (emails.length) {
				options.push({
					id: 2,
					label: 'Эл. почта',
					value: emails.join(' • '),
				});
			}

			if (sites.length) {
				options.push({
					id: 3,
					label: 'Ссылки',
					value: sites.join(' • '),
				});
			}

			if (options.length) {
				result = {
					groupIndex: 3,
					entries: [
						{
							chip: paramsChips.change,
							typoData: { options },
						},
					],
				};
			}
		}
	}

	const payloadId = item.id || crypto.randomUUID();
	const nextItem = { ...item, id: payloadId };

	if (!result) {
		return { groups, item: nextItem, skipped: false };
	}

	const { groupIndex } = result;
	const entries = result.entries
		? result.entries
		: [
			{
				title: result.title,
				chip: chips[item.action] || result.typoData?.chip,
				typoData: result.typoData,
			},
		];

	if (groupIndex == null || !entries.length) {
		return { groups, item: nextItem, skipped: false };
	}

	const nextTypos = entries
		.filter((entry) => entry?.typoData)
		.map((entry) => ({
			title: entry.title,
			chip: entry.chip || chips[item.action],
			editable: true,
			payloadId,
			...entry.typoData,
		}));

	if (!nextTypos.length) {
		return { groups, item: nextItem, skipped: false };
	}

	const nextGroups = groups.map((group, index) =>
		index === groupIndex
			? { ...group, typos: [...group.typos, ...nextTypos] }
			: group,
	);

	return { groups: nextGroups, item: nextItem, skipped: false };
};

export const buildStateFromPayloadItems = (
	initialPayloadItems = [],
	{ force = false } = {},
) => {
	let groups = createInitialState();
	const payloadItems = [];

	for (const item of initialPayloadItems) {
		const result = applyPayloadItem(groups, item, { force });

		if (result.skipped) continue;

		groups = result.groups;
		payloadItems.push(result.item);
	}

	return { groups, payloadItems };
};

export const useEventFormGroups = (initialPayloadItems = []) => {
	// groups[0] - objections
	// groups[1] - contacts
	// groups[2] - docs
	// groups[3] - area params
	// groups[4] - connected areas
	const seeded = useMemo(
		() =>
			buildStateFromPayloadItems(initialPayloadItems, {
				force: initialPayloadItems.length > 0,
			}),
		// Seed only on mount; remount via key when editing another activity.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);
	const [groups, setGroups] = useState(() => seeded.groups);
	const [payloadItems, setPayloadItems] = useState(() => seeded.payloadItems);

	const removeFromGroup = useCallback(
		({ group: groupId, item: itemIndex }) => {
			setGroups((prevGroups) => {
				const group = prevGroups.find((entry) => entry.id === groupId);
				const typo = group?.typos[itemIndex];
				const payloadId = typo?.payloadId;

				if (!payloadId) return prevGroups;

				setPayloadItems((items) =>
					items.filter((item) => item.id !== payloadId),
				);

				return prevGroups.map((entry) =>
					entry.id === groupId
						? {
							...entry,
							typos: entry.typos.filter(
								(item) => item.payloadId !== payloadId,
							),
						}
						: entry,
				);
			});
		},
		[],
	);

	const groupsWithHandlers = useMemo(
		() =>
			groups.map((group) => ({
				...group,
				onDelete: removeFromGroup,
			})),
		[groups, removeFromGroup],
	);

	const addDataToGroup = (type, action, data, initialData) => {
		const handler = typeHandlers[type];
		const preview = handler?.(action, data, initialData);
		const replaceExisting = type === 'params';
		const paramsGroupIndex = 3;

		// Revert to baseline: drop previously staged params instead of keeping them.
		if (action === 'change' && handler && !preview) {
			if (!replaceExisting) return;

			setPayloadItems((prev) =>
				prev.filter((item) => item.type !== type),
			);
			setGroups((prev) =>
				prev.map((group, index) =>
					index === paramsGroupIndex
						? { ...group, typos: [] }
						: group,
				),
			);
			return;
		}

		const payloadId = crypto.randomUUID();

		setPayloadItems((prev) => {
			const next = replaceExisting
				? prev.filter((item) => item.type !== type)
				: prev;

			return [...next, { id: payloadId, type, action, data, initialData }];
		});

		if (!preview) return;

		const { groupIndex } = preview;
		const entries = preview.entries
			? preview.entries
			: [
				{
					title: preview.title,
					chip: chips[action] || preview.typoData?.chip,
					typoData: preview.typoData,
				},
			];

		if (groupIndex == null || !entries.length) return;

		const nextTypos = entries
			.filter((entry) => entry?.typoData)
			.map((entry) => ({
				title: entry.title,
				chip: entry.chip || chips[action],
				editable: true,
				payloadId,
				...entry.typoData,
			}));

		if (!nextTypos.length) return;

		setGroups((prev) =>
			prev.map((group, index) => {
				if (index !== groupIndex) return group;

				return {
					...group,
					typos: replaceExisting
						? nextTypos
						: [...group.typos, ...nextTypos],
				};
			}),
		);
	};

	const resetGroups = () => {
		setGroups(createInitialState());
		setPayloadItems([]);
	};

	return {
		groups: groupsWithHandlers,
		payloadItems,
		addDataToGroup,
		removeFromGroup,
		resetGroups,
	};
};
